import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { DV2 } from "../DeclarationV2/theme";
import { monthName } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, Card, TextLink, useDv2Theme } from "../DeclarationV2/layout/Ui";
import { coursesHistoryPath, coursesTabPath } from "../CoursesV2/tabs";

/**
 * Encadré d'explication de la « Déclaration rapide des cours du mois »
 * (anciens brouillons) : à quoi elle sert, comment elle fonctionne, et le
 * rappel que les cours déjà déclarés sont dans « Ce mois » et l'Historique.
 * Deux accès en un clic : les cours du mois et l'historique du mois précédent.
 */
const QuickDeclarationExplainer = () => {
    const { primary, fontFamily, text } = useDv2Theme();
    const navigate = useNavigate();

    const courses = vocab.courses();
    const month = monthName(moment());
    const previous = monthName(moment().subtract(1, "month"));
    const previousPeriod = moment().subtract(1, "month").format("YYYY-MM");
    const declaredPlural = `${vocab.declared()}s`;

    const steps = [
        <>Vérifiez chaque brouillon : c'est une proposition, pas encore une déclaration.</>,
        <>Modifiez la date, l'horaire ou la durée si besoin, et supprimez les {courses} qui n'auront pas lieu.</>,
        <>Cliquez sur <strong>Déclarer</strong> : le brouillon devient {vocab.aCourse()} {vocab.declared()}, exactement comme si vous l'aviez saisi vous-même. Rien n'est transmis tant que vous ne déclarez pas.</>,
    ];

    return (
        <Card tint as="aside" padding={0} style={{ overflow: "hidden", fontFamily, color: text }}>
            <div className="flex flex-col gap-4" style={{ padding: "18px 20px" }}>
                <div>
                    <h2 id="quick-declaration-title" style={{ fontSize: 17, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>
                        Déclaration rapide : comment ça marche ?
                    </h2>
                    <p style={{ fontSize: 14, margin: "6px 0 0", lineHeight: 1.55 }}>
                        Pour vous faire gagner du temps, vos {courses} {declaredPlural} en {previous} sont repris ici en brouillon pour {month}, avec les mêmes {vocab.clients()}, jours, horaires et durées. Vous n'avez plus qu'à vérifier et déclarer.
                    </p>
                </div>

                <ol className="flex flex-col gap-2" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {steps.map((step, index) => (
                        <li key={index} className="flex gap-3 items-start" style={{ fontSize: 14, lineHeight: 1.55 }}>
                            <span aria-hidden style={{ width: 24, height: 24, borderRadius: 999, background: primary, color: "#fff", fontWeight: 800, fontSize: 13, display: "grid", placeItems: "center", flexShrink: 0, marginTop: 1 }}>
                                {index + 1}
                            </span>
                            <span>{step}</span>
                        </li>
                    ))}
                </ol>

                <div style={{ background: DV2.surface, borderRadius: 12, padding: "12px 14px", fontSize: 14, lineHeight: 1.55, borderLeft: `4px solid ${primary}` }}>
                    <strong>Vos {courses} de {previous} ont bien été {declaredPlural}.</strong>{" "}
                    Les brouillons n'en sont qu'une copie pour {month}. Les {courses} déjà {declaredPlural} se retrouvent dans <strong>Ce mois</strong>, puis dans l'<strong>Historique</strong> une fois passés.
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <Button label={`Voir mes ${courses} du mois`} onClick={() => navigate(coursesTabPath("ce-mois"))} />
                    <TextLink label={`Voir l'historique de ${previous}`} size={14} onClick={() => navigate(coursesHistoryPath(previousPeriod))} />
                </div>
            </div>
        </Card>
    );
};

export default QuickDeclarationExplainer;
